import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FastAPIClient from "../../client";
import config from "../../config";
import CustomBarChart from "./CustomBarChart";
import StatCard from "../LayoutComponents/StatCard";

const client = new FastAPIClient(config);

type UserKilometerLog = {
    user_id: string;
    first_name: string;
    last_name: string;
    activity_time: string;
    kilometers: number;
    team_name: string;
    warehouse_name: string;
};

interface UserKilometerProps {
    selectedUser: string;
    selectedTeam: string;
    selectedWarehouse: string;
    dateRange: [Dayjs | null, Dayjs | null];
}

const UserKilometers: React.FC<UserKilometerProps> = ({
    selectedUser,
    selectedTeam,
    selectedWarehouse,
    dateRange,
}) => {
    const [data, setData] = useState<UserKilometerLog[] | null>(null);

    useEffect(() => {
        void fetchKilometers();
    }, []);

    const fetchKilometers = async (): Promise<void> => {
        try {
            const response = await client.fetchUserKilometers();
            setData(response.results);
        } catch (error) {
            console.error("Error fetching kilometers:", error);
        }
    };

    const filteredData = useMemo(() => {
        if (!data || !dateRange[0] || !dateRange[1]) return [];

        const [start, end] = dateRange;

        return data.filter((d) => {
            const inRange = dayjs(d.activity_time).isBetween(start, end, null, "[]");
            const matchesTeam = selectedTeam === "all" || d.team_name === selectedTeam;
            const matchesUser = selectedUser === "all" || `${d.first_name} ${d.last_name}` === selectedUser;
            const matchesWarehouse = selectedWarehouse === "all" || d.warehouse_name === selectedWarehouse;

            return inRange && matchesTeam && matchesUser && matchesWarehouse;
        });
    }, [data, selectedUser, selectedTeam, selectedWarehouse, dateRange]);

    const chartData = useMemo(() => {
        if (!filteredData.length) return { labels: [], values: [], seriesLabels: [] };

        const allDates = Array.from(
            new Set(
                filteredData.map((entry) =>
                    dayjs(entry.activity_time).format("YYYY-MM-DD")
                )
            )
        ).sort();

        const valueMap = new Map<
            string,
            Map<
                string,
                {
                    minTime: string;
                    minKilometers: number;
                    maxTime: string;
                    maxKilometers: number;
                }
            >
        >();

        filteredData.forEach((entry) => {
            const entryDate = dayjs(entry.activity_time).format("YYYY-MM-DD");
            if (!valueMap.has(entryDate)) {
                valueMap.set(entryDate, new Map());
            }

            const dailyData = valueMap.get(entryDate);
            if (dailyData) {
                const userId = entry.user_id;
                const currentData = dailyData.get(userId) || {
                    minTime: "",
                    minKilometers: Infinity,
                    maxTime: "",
                    maxKilometers: -Infinity,
                };

                const currentTime = entry.activity_time;
                const currentKilometers = entry.kilometers;

                if (currentTime < currentData.minTime || currentData.minTime === "") {
                    currentData.minTime = currentTime;
                    currentData.minKilometers = currentKilometers;
                }
                if (currentTime > currentData.maxTime || currentData.maxTime === "") {
                    currentData.maxTime = currentTime;
                    currentData.maxKilometers = currentKilometers;
                }

                dailyData.set(userId, currentData);
            }
        });

        const userNames = Array.from(
            new Set(
                filteredData.map(
                    (entry) => `${entry.first_name} ${entry.last_name}`
                )
            )
        );

        const values = allDates.map((date) => {
            const dailyData = valueMap.get(date);
            if (!dailyData) return userNames.map(() => 0);

            return userNames.map((fullName) => {
                const user = filteredData.find(
                    (entry) => `${entry.first_name} ${entry.last_name}` === fullName
                );
                if (user) {
                    const data = dailyData.get(user.user_id);
                    if (data) {
                        return data.maxKilometers - data.minKilometers;
                    }
                }
                return 0;
            });
        });

        return { labels: allDates, values, seriesLabels: userNames };
    }, [filteredData]);

    return (
        <StatCard
            title="Kilomètres parcourus"
            value=""
            subtitle=""
            loading={!data}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CustomBarChart
                    chartData={{
                        labels: chartData.labels,
                        values: chartData.values,
                        seriesLabels: chartData.seriesLabels,
                        showFullDateLabels: true,
                        showSeriesLabels: true,
                    }}
                />
            </LocalizationProvider>
        </StatCard>
    );
};

export default UserKilometers;
